import api from './api';
import type { RateQuote } from '@/types';

export type MiscDirection = 'outbound' | 'return';

export interface MiscAddress {
  name: string;
  company?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  countryCode: string;
  phone: string;
  email?: string;
}

export interface MiscPackage {
  weight: number;
}

export interface MiscShipment {
  id: string;
  direction: MiscDirection;
  address: MiscAddress;
  clinicId: string | null;
  patientId: string | null;
  contents: string;
  comment: string;
  chargeAmount: string | number | null;
  carrier: string;
  carrierCode: string | null;
  serviceCode: string | null;
  serviceName: string | null;
  trackingNumber: string | null;
  shipmentCost: string | number | null;
  costCurrency: string;
  status: 'label_created' | 'voided';
  createdBy: string | null;
  createdAt: string;
  hasLabel: boolean;
}

export interface MiscRatesRequest {
  direction: MiscDirection;
  address: MiscAddress;
  packages?: MiscPackage[];
}

export interface CreateMiscShipmentRequest extends MiscRatesRequest {
  contents: string;
  comment: string;
  chargeAmount?: number;
  clinicId?: string;
  patientId?: string;
  provider?: string;
  carrierCode?: string;
  serviceCode?: string;
  serviceName?: string;
}

export const miscShipmentsApi = {
  list: async (params: { search?: string; skip?: number; take?: number } = {}) => {
    const response = await api.get<{ data: MiscShipment[]; total: number; skip: number; take: number }>(
      '/shipping/misc',
      { params },
    );
    return response.data;
  },

  getRates: async (dto: MiscRatesRequest) => {
    const response = await api.post<RateQuote[]>('/shipping/misc/rates', dto);
    return response.data;
  },

  create: async (dto: CreateMiscShipmentRequest) => {
    const response = await api.post<MiscShipment>('/shipping/misc', dto);
    return response.data;
  },

  void: async (id: string) => {
    const response = await api.post<{ success: boolean; message: string }>(`/shipping/misc/${id}/void`);
    return response.data;
  },

  /** Authenticated fetch + open: a plain link would not carry the session token. */
  openLabel: async (id: string) => {
    const response = await api.get(`/shipping/misc/${id}/label`, { responseType: 'blob' });
    window.open(URL.createObjectURL(response.data as Blob), '_blank');
  },
};

export default miscShipmentsApi;
